/* eslint-disable */
import { invariant, warning } from './history';
var ResultType;
/**
 * Successful result from a loader or action
 */
(function (ResultType) {
  ResultType["data"] = "data";
  ResultType["deferred"] = "deferred";
  ResultType["redirect"] = "redirect";
  ResultType["error"] = "error";
})(ResultType || (ResultType = {}));
/**
 * Successful defer() result from a loader or action
 */
/**
 * Redirect result from a loader or action
 */
/**
 * Unsuccessful result from a loader or action
 */
/**
 * Result from a loader or action - potentially successful or unsuccessful
 */
/**
 * Active navigation/fetcher form methods are exposed in lowercase on the
 * RouterState
 */
/**
 * In v7, active navigation/fetcher form methods are exposed in uppercase on the
 * RouterState.  This is to align with the normalization done via fetch().
 */
/**
 * @private
 * Internal interface to pass around for action submissions, not intended for
 * external consumption
 */
/**
 * @private
 * Arguments passed to route loader/action functions.  Same for now but we keep
 * this as a private implementation detail in case they diverge in the future.
 */
/**
 * Arguments passed to loader functions
 */
/**
 * Arguments passed to action functions
 */
/**
 * Loaders and actions can return anything except `undefined` (`null` is a
 * valid return value if there is no data to return).  Responses are preferred
 * and will ease any future migration to Remix
 */
/**
 * Route loader function signature
 */
/**
 * Route action function signature
 */
/**
 * Route shouldRevalidate function signature.  This runs after any submission
 * (navigation or fetcher), so we flatten the navigation/fetcher submission
 * onto the arguments.  It shouldn't matter whether it came from a navigation
 * or a fetcher, what really matters is the URLs and the formData since loaders
 * have to re-run based on the data models that were potentially mutated.
 */
/**
 * Keys we cannot change from within a lazy() function. We spread all other keys
 * onto the route. Either they're meaningful to the router, or they'll get
 * ignored.
 */
/**
 * lazy() function to load a route definition, which can add non-matching
 * related properties to a route
 */
/**
 * Base RouteObject with common props shared by all types of routes
 */
/**
 * Index routes must not have children
 */
/**
 * Non-index routes may have children, but cannot have index
 */
/**
 * A route object represents a logical route, with (optionally) its child
 * routes organized in a tree-like structure.
 */
/**
 * A data route object, which is just a RouteObject with a required unique ID
 */
// Recursive helper for finding path parameters in the absence of wildcards
/**
 * Examples:
 * "/a/b/*" -> "*"
 * ":a" -> "a"
 * "/a/:b" -> "b"
 * "/a/blahblahblah:b" -> "b"
 * "/:a/:b" -> "a" | "b"
 * "/:a/b/:c/*" -> "a" | "c" | "*"
 */
// Attempt to parse the given string segment. If it fails, then just return the
// plain string type as a default fallback. Otherwise return the union of the
// parsed string literals that were referenced as dynamic segments in the route.
/**
 * The parameters that were parsed from the URL path.
 */
/**
 * A RouteMatch contains info about how a route matched a URL.
 */
/**
 * Returns a path with params interpolated.
 *
 * @see https://reactrouter.com/en/main/utils/generate-path
 */
export function generatePath(originalPath, params = {}) {
  let path = originalPath;
  if (path.endsWith('*') && path !== '*' && !path.endsWith('/*')) {
    warning(false, `Route path "${path}" will be treated as if it were ` + `"${path.replace(/\*$/, '/*')}" because the \`*\` character must ` + `always follow a \`/\` in the pattern. To get rid of this warning, ` + `please change the route path to "${path.replace(/\*$/, '/*')}".`);
    path = path.replace(/\*$/, '/*');
  }

  // ensure `/` is added at the beginning if the path is absolute
  const prefix = path.startsWith('/') ? '/' : '';
  const segments = path.split(/\/+/).map((segment, index, array) => {
    const isLastSegment = index === array.length - 1;

    // only apply the splat if it's the last segment
    if (isLastSegment && segment === '*') {
      const star = '*';
      const starParam = params[star];

      // Apply the splat
      return starParam;
    }
    const keyMatch = segment.match(/^:(\w+)(\??)$/);
    if (keyMatch) {
      const [, key, optional] = keyMatch;
      let param = params[key];
      if (optional === '?') {
        return param == null ? '' : param;
      }
      if (param == null) {
        invariant(false, `Missing ":${key}" param`);
      }
      return param;
    }

    // Remove any optional markers from optional static segments
    return segment.replace(/\?$/g, '');
  })
  // Remove empty segments
  .filter(segment => !!segment);
  return prefix + segments.join('/');
}

/**
 * A PathPattern is used to match on some portion of a URL pathname.
 */

/**
 * A PathMatch contains info about how a PathPattern matched on a URL pathname.
 */

/**
 * Performs pattern matching on a URL pathname and returns information about
 * the match.
 *
 * @see https://reactrouter.com/en/main/utils/match-path
 */
export function matchPath(pattern, pathname) {
  if (typeof pattern === 'string') {
    pattern = {
      path: pattern,
      caseSensitive: false,
      end: true
    };
  }
  let [matcher, paramNames] = compilePath(pattern.path, pattern.caseSensitive, pattern.end);
  let match = pathname.match(matcher);
  if (!match) return null;
  let matchedPathname = match[0];
  let pathnameBase = matchedPathname.replace(/(.)\/+$/, '$1');
  let captureGroups = match.slice(1);
  let params = paramNames.reduce((memo, paramName, index) => {
    // We need to compute the pathnameBase here using the raw splat value
    // instead of using params["*"] later because it will be decoded then
    if (paramName === '*') {
      let splatValue = captureGroups[index] || '';
      pathnameBase = matchedPathname.slice(0, matchedPathname.length - splatValue.length).replace(/(.)\/+$/, '$1');
    }
    memo[paramName] = safelyDecodeURIComponent(captureGroups[index] || '', paramName);
    return memo;
  }, {});
  return {
    params,
    pathname: matchedPathname,
    pathnameBase,
    pattern
  };
}
function compilePath(path, caseSensitive = false, end = true) {
  warning(path === '*' || !path.endsWith('*') || path.endsWith('/*'), `Route path "${path}" will be treated as if it were ` + `"${path.replace(/\*$/, '/*')}" because the \`*\` character must ` + `always follow a \`/\` in the pattern. To get rid of this warning, ` + `please change the route path to "${path.replace(/\*$/, '/*')}".`);
  let paramNames = [];
  let regexpSource = '^' + path.replace(/\/*\*?$/, '') // Ignore trailing / and /*, we'll handle it below
  .replace(/^\/*/, '/') // Make sure it has a leading /
  .replace(/[\\.*+^$?{}|()[\]]/g, '\\$&') // Escape special regex chars
  .replace(/\/:(\w+)/g, (_, paramName) => {
    paramNames.push(paramName);
    return '/([^\\/]+)';
  });
  if (path.endsWith('*')) {
    paramNames.push('*');
    regexpSource += path === '*' || path === '/*' ? '(.*)$' // Already matched the initial /, just match the rest
    : '(?:\\/(.+)|\\/*)$'; // Don't include the / in params["*"]
  } else if (end) {
    // When matching to the end, ignore trailing slashes
    regexpSource += '\\/*$';
  } else if (path !== '' && path !== '/') {
    // If our path is non-empty and contains anything beyond an initial slash,
    // then we have _some_ form of path in our regex so we should expect to
    // match only if we find the end of this path segment.  Look for an optional
    // non-captured trailing slash (to match a portion of the URL) or the end
    // of the path (if we've matched to the end).  We used to do this with a
    // word boundary but that gives false positives on routes like
    // /user-preferences since `-` counts as a word boundary.
    regexpSource += '(?:(?=\\/|$))';
  } else {
    // Nothing to match for "" or "/"
  }
  let matcher = new RegExp(regexpSource, caseSensitive ? undefined : 'i');
  return [matcher, paramNames];
}
function safelyDecodeURIComponent(value, paramName) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    warning(false, `The value for the URL param "${paramName}" will not be decoded because` + ` the string "${value}" is a malformed URL segment. This is probably` + ` due to a bad percent encoding (${error}).`);
    return value;
  }
}
class AbortedDeferredError extends Error {}
class DeferredData {
  constructor(data, responseInit) {
    this.pendingKeysSet = new Set();
    this.controller = void 0;
    this.abortPromise = void 0;
    this.unlistenAbortSignal = void 0;
    this.subscribers = new Set();
    this.data = void 0;
    this.init = void 0;
    this.deferredKeys = [];
    invariant(data && typeof data === 'object' && !Array.isArray(data), 'defer() only accepts plain objects');

    // Set up an AbortController + Promise we can race against to exit early
    // cancellation
    let reject;
    this.abortPromise = new Promise((_, r) => reject = r);
    this.controller = new AbortController();
    let onAbort = () => reject(new AbortedDeferredError('Deferred data aborted'));
    this.unlistenAbortSignal = () => this.controller.signal.removeEventListener('abort', onAbort);
    this.controller.signal.addEventListener('abort', onAbort);
    this.data = Object.entries(data).reduce((acc, [key, value]) => Object.assign(acc, {
      [key]: this.trackPromise(key, value)
    }), {});
    if (this.done) {
      // All incoming values were resolved
      this.unlistenAbortSignal();
    }
    this.init = responseInit;
  }
  trackPromise(key, value) {
    if (!(value instanceof Promise)) {
      return value;
    }
    this.deferredKeys.push(key);
    this.pendingKeysSet.add(key);

    // We store a little wrapper promise that will be extended with
    // _data/_error props upon resolve/reject
    let promise = Promise.race([value, this.abortPromise]).then(data => this.onSettle(promise, key, null, data), error => this.onSettle(promise, key, error));

    // Register rejection listeners to avoid uncaught promise rejections on
    // errors or aborted deferred values
    promise.catch(() => {});
    Object.defineProperty(promise, '_tracked', {
      get: () => true
    });
    return promise;
  }
  onSettle(promise, key, error, data) {
    if (this.controller.signal.aborted && error instanceof AbortedDeferredError) {
      this.unlistenAbortSignal();
      Object.defineProperty(promise, '_error', {
        get: () => error
      });
      return Promise.reject(error);
    }
    this.pendingKeysSet.delete(key);
    if (this.done) {
      // Nothing left to abort!
      this.unlistenAbortSignal();
    }
    if (error) {
      Object.defineProperty(promise, '_error', {
        get: () => error
      });
      this.emit(false, key);
      return Promise.reject(error);
    }
    Object.defineProperty(promise, '_data', {
      get: () => data
    });
    this.emit(false, key);
    return data;
  }
  emit(aborted, settledKey) {
    this.subscribers.forEach(subscriber => subscriber(aborted, settledKey));
  }
  subscribe(fn) {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }
  cancel() {
    this.controller.abort();
    this.pendingKeysSet.forEach((v, k) => this.pendingKeysSet.delete(k));
    this.emit(true);
  }
  async resolveData(signal) {
    let aborted = false;
    if (!this.done) {
      let onAbort = () => this.cancel();
      signal.addEventListener('abort', onAbort);
      aborted = await new Promise(resolve => {
        this.subscribe(aborted => {
          signal.removeEventListener('abort', onAbort);
          if (aborted || this.done) {
            resolve(aborted);
          }
        });
      });
    }
    return aborted;
  }
  get done() {
    return this.pendingKeysSet.size === 0;
  }
  get unwrappedData() {
    invariant(this.data !== null && this.done, 'Can only unwrap data on initialized and settled deferreds');
    return Object.entries(this.data).reduce((acc, [key, value]) => Object.assign(acc, {
      [key]: unwrapTrackedPromise(value)
    }), {});
  }
  get pendingKeys() {
    return Array.from(this.pendingKeysSet);
  }
}
function isTrackedPromise(value) {
  return value instanceof Promise && value._tracked === true;
}
function unwrapTrackedPromise(value) {
  if (!isTrackedPromise(value)) {
    return value;
  }
  if (value._error) {
    throw value._error;
  }
  return value._data;
}